import { createSelector } from 'reselect';
import {
    attractionsUrl,
    destinationUrl,
    destinationStoriesUrl,
    destinationBlogsUrl,
    destinationPartnersUrl,
} from '@ts/utils/urls';

import slugify from '@ts/utils/slugify';

const key = 'app';
const domains = {
    tick: (state) => state.global[key].tick,
    categories: (state) => state.global[key].categories,
    destinations: (state) => state.global[key].destinations,
    isFirstRender: (state) => state.global[key].isFirstRender,
    prefetchProgress: (state) => state.global[key].prefetchProgress,
};

const selectorTick = createSelector([domains.tick], (tick) => tick);

const selectorDestinations = createSelector([domains.destinations], (destinations) =>
    destinations.map((destination) => ({
        ...destination,
        url: destinationUrl({
            state: destination.state.code,
            slug: destination.slug,
        }),
        url_stories: destinationStoriesUrl({
            state: destination.state.code,
            slug: destination.slug,
        }),
        url_blog: destinationBlogsUrl({
            state: destination.state.code,
            slug: destination.slug,
        }),
        url_search: attractionsUrl({
            destination: destination.slug,
        }),
        url_partners: destinationPartnersUrl({
            slug: destination.slug,
        }),
    }))
);

const selectorDestinationsAsObject = createSelector([selectorDestinations], (destinations) => {
    const asObject = {};
    destinations.forEach((destination) => {
        const slug = (destination.slug || slugify(destination.name)).toUpperCase();
        asObject[destination.id] = destination;
        asObject[slug] = destination;
        asObject[`${destination.state.code.toUpperCase()}_${slug}`] = destination;
    });
    return asObject;
});

const selectorCategories = createSelector([domains.categories], (categories) => categories);

const selectorCategoriesAsObject = createSelector([selectorCategories], (categories) => {
    const asObject = {};
    categories.forEach((category) => {
        const slug = (category.slug || slugify(category.name)).toUpperCase();
        asObject[category.id] = category;
        asObject[slug.toUpperCase()] = category;
    });
    return asObject;
});

const selectorIsFirstRender = createSelector(
    [domains.isFirstRender],
    (isFirstRender) => isFirstRender
);

const selectorPrefetchProgressPercent = createSelector(
    [domains.prefetchProgress],
    (prefetchProgress) =>
        prefetchProgress.total > 0 ? (100 * prefetchProgress.loaded) / prefetchProgress.total : 0
);

const selectorPrefetchProgressLoading = createSelector(
    [domains.prefetchProgress],
    (prefetchProgress) => prefetchProgress.loading
);

const makeSelectTick = () => selectorTick;
const makeSelectDestinations = () => selectorDestinations;
const makeSelectDestinationsAsObject = () => selectorDestinationsAsObject;
const makeSelectCategories = () => selectorCategories;
const makeSelectCategoriesAsObject = () => selectorCategoriesAsObject;
const makeSelectIsFirstRender = () => selectorIsFirstRender;
const makeSelectPrefetchProgressPercent = () => selectorPrefetchProgressPercent;
const makeSelectPrefetchProgressLoading = () => selectorPrefetchProgressLoading;

export {
    makeSelectCategories,
    makeSelectCategoriesAsObject,
    makeSelectDestinations,
    makeSelectDestinationsAsObject,
    makeSelectIsFirstRender,
    makeSelectPrefetchProgressLoading,
    makeSelectPrefetchProgressPercent,
    makeSelectTick,
};
